import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Grid,
  Divider
} from '@mui/material';
import { Vacancy } from '../types/vacancy';
import {
  Facebook,
  Twitter,
  LinkedIn,
  WhatsApp,
  Telegram,
  ContentCopy,
  Check,
} from '@mui/icons-material';

interface Props {
  open: boolean;
  onClose: () => void;
  vacancy: Vacancy | null;
}

const VacancyDetail: React.FC<Props> = ({ open, onClose, vacancy }) => {
  const [copied, setCopied] = useState(false);
  if (!vacancy) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareText = vacancy.job_title;
  const absoluteUrl =
    typeof window !== 'undefined' ? window.location.href : '';
  const encodedUrl = encodeURIComponent(absoluteUrl);
  const encodedText = encodeURIComponent(shareText);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(absoluteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div">
          {vacancy.job_title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Job Details */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Job Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Job Type
                </Typography>
                <Chip label={vacancy.job_type} color="primary" size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Job Site
                </Typography>
                <Chip label={vacancy.job_site} color="secondary" size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Experience Level
                </Typography>
                <Chip label={vacancy.experience_level} color="info" size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Education Required
                </Typography>
                <Chip label={vacancy.education} color="warning" size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Gender Preference
                </Typography>
                <Chip label={vacancy.gender_preference} color="default" size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Number of Vacancies
                </Typography>
                <Chip label={vacancy.vacancies.toString()} color="success" size="small" />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Job Description */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Job Description
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {vacancy.job_description}
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Share this vacancy
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Help spread the word
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Facebook />}
                href={shareLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
              >
                Facebook
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Telegram />}
                href={shareLinks.telegram}
                target="_blank"
                rel="noopener noreferrer"
              >
                Telegram
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Twitter />}
                href={shareLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
              >
                Twitter
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<LinkedIn />}
                href={shareLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<WhatsApp />}
                href={shareLinks.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
              >
                WhatsApp
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={copied ? <Check /> : <ContentCopy />}
                onClick={copyToClipboard}
              >
                {copied ? 'Copied' : 'Copy Link'}
              </Button>
            </Box>
          </Box>

          <Divider />

          {/* Important Dates */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Important Dates
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Application Deadline
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="error">
                  {formatDate(vacancy.deadline)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Date Posted
                </Typography>
                <Typography variant="body1">
                  {formatDate(vacancy.date_posted)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VacancyDetail;
